(() => {
  if (customElements.get('crosight-review-marquee')) return;

  class CrosightReviewMarquee extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = true;
      const previousRows = this.rows || [];
      this.rows = Array.from(this.querySelectorAll('.review-marquee__row'), row => {
        const track = row.querySelector('.review-marquee__track');
        const group = track.firstElementChild;
        const previous = previousRows.find(state => state.row === row);
        const top = row.classList.contains('review-marquee__row--top');
        const state = { row, track, group, length: previous?.length || 0,
          position: previous?.position || 0, direction: top ? -1 : 1,
          hovered: matchMedia('(hover: hover)').matches && row.matches(':hover'),
          pointer: null, frame: 0, timer: 0, resumeAt: 0, lastTime: null,
          duration: Math.max(1, Number(top ? this.dataset.topDuration : this.dataset.bottomDuration) || (top ? 70 : 64)) };
        row.addEventListener('pointerdown', event => this.begin(event, state), options);
        row.addEventListener('pointermove', event => this.move(event, state), options);
        for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
          row.addEventListener(type, event => this.end(event, state), options);
        }
        row.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse') return;
          state.hovered = true;
          this.stop(state);
        }, options);
        row.addEventListener('pointerleave', event => {
          if (event.pointerType !== 'mouse') return;
          state.hovered = false;
          this.resumeLater(state);
        }, options);
        row.addEventListener('dragstart', event => event.preventDefault(), options);
        return state;
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.resumeLater();
      }, options);
      this.motion.addEventListener('change', () => {
        this.stop();
        if (!this.motion.matches) this.resumeLater();
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.rows.forEach(state => {
        this.resizeObserver.observe(state.group);
        this.resizeObserver.observe(state.row);
      });
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.start();
        else this.stop();
      });
      this.intersectionObserver.observe(this);
      this.measure();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.rows?.forEach(state => {
        state.pointer = null;
        state.row.removeAttribute('data-dragging');
      });
    }

    normalize(position, length) {
      return length > 0 ? ((position % length) + length) % length : 0;
    }

    render(state) {
      state.position = this.normalize(state.position, state.length);
      state.track.style.transform = `translate3d(${-state.position}px, 0, 0)`;
    }

    measure() {
      this.rows.forEach(state => {
        const length = state.group.getBoundingClientRect().width;
        if (!length) return;
        if (state.length) state.position *= length / state.length;
        state.length = length;
        const copies = Math.max(2, Math.ceil(state.row.clientWidth / length) + 1);
        while (state.track.children.length < copies) {
          const clone = state.group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[data-shopify-editor-block]').forEach(tile => tile.removeAttribute('data-shopify-editor-block'));
          state.track.appendChild(clone);
        }
        while (state.track.children.length > copies) state.track.lastElementChild.remove();
        this.render(state);
      });
    }

    canRun(state) {
      return this.isConnected && this.inView && !document.hidden && !this.motion.matches && !state.hovered && !state.pointer;
    }

    stop(state) {
      if (!state) {
        this.rows?.forEach(row => this.stop(row));
        return;
      }
      clearTimeout(state.timer);
      state.timer = 0;
      cancelAnimationFrame(state.frame);
      state.frame = 0;
      state.lastTime = null;
    }

    start(state) {
      if (!state) {
        this.rows?.forEach(row => this.start(row));
        return;
      }
      if (state.frame || state.timer || !this.canRun(state)) return;
      const delay = state.resumeAt - performance.now();
      if (delay > 0) {
        state.timer = setTimeout(() => {
          state.timer = 0;
          this.start(state);
        }, delay);
        return;
      }
      state.lastTime = null;
      state.frame = requestAnimationFrame(time => this.tick(time, state));
    }

    resumeLater(state) {
      if (!state) {
        this.rows?.forEach(row => this.resumeLater(row));
        return;
      }
      this.stop(state);
      state.resumeAt = performance.now() + 1000;
      this.start(state);
    }

    tick(time, state) {
      state.frame = 0;
      if (!this.canRun(state)) {
        state.lastTime = null;
        return;
      }
      const elapsed = state.lastTime === null ? 0 : Math.min(time - state.lastTime, 64) / 1000;
      state.lastTime = time;
      state.position += state.direction * state.length / state.duration * elapsed;
      this.render(state);
      state.frame = requestAnimationFrame(nextTime => this.tick(nextTime, state));
    }

    mouseInside(event, state) {
      const rect = state.row.getBoundingClientRect();
      return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    }

    begin(event, state) {
      if (state.pointer || !event.isPrimary || event.button !== 0) return;
      this.stop(state);
      state.pointer = { id: event.pointerId, x: event.clientX, startX: event.clientX, startY: event.clientY, axis: event.pointerType === 'mouse' ? 'x' : null };
      if (event.pointerType === 'mouse') state.hovered = this.mouseInside(event, state);
      state.row.setPointerCapture(event.pointerId);
      state.row.setAttribute('data-dragging', '');
    }

    move(event, state) {
      const pointer = state.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      if (pointer.axis === null) {
        const dx = event.clientX - pointer.startX;
        const dy = event.clientY - pointer.startY;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 5) return;
        pointer.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (pointer.axis !== 'x') return;
      if (event.cancelable) event.preventDefault();
      state.position -= event.clientX - pointer.x;
      pointer.x = event.clientX;
      this.render(state);
    }

    end(event, state) {
      const pointer = state.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      state.pointer = null;
      state.row.removeAttribute('data-dragging');
      if (event.pointerType === 'mouse') state.hovered = this.mouseInside(event, state);
      if (state.row.hasPointerCapture(event.pointerId)) state.row.releasePointerCapture(event.pointerId);
      this.resumeLater(state);
    }
  }

  customElements.define('crosight-review-marquee', CrosightReviewMarquee);
})();
